import { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { loadGroupBySlug, regenerateInvite, removeMember, setMemberRole, leaveGroup, uploadGroupLogo, loadJoinRequests, approveJoinRequest, rejectJoinRequest, requestJoinGroupById, loadMyPendingRequests, type TipGroup, type GroupMember, type JoinRequest } from "@/lib/groups";
import { loadRanking, type RankRow } from "@/lib/ranking";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Upload, Crown, UserMinus, RefreshCw, LogOut, ChevronLeft, Link2, Check, X, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { UserAvatar } from "@/components/UserAvatar";
import { GroupLogo } from "@/components/GroupLogo";
import { InviteButton } from "@/components/InviteButton";
import { buildInviteUrl } from "@/lib/share";

export default function Group() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [group, setGroup] = useState<TipGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [rows, setRows] = useState<RankRow[]>([]);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = async () => {
    if (!slug) return;
    setLoading(true);
    const data = await loadGroupBySlug(slug);
    if (!data) { setNotFound(true); setLoading(false); return; }
    setGroup(data.group);
    setMembers(data.members);
    const memberIds = data.members.map(m => m.user_id);
    const allRanks = await loadRanking(memberIds);
    setRows(allRanks);
    if (data.group.name) document.title = `${data.group.name} – Tippgruppe`;
    // Lade offene Beitrittsanfragen (nur Admins erhalten Daten via RLS)
    try {
      const reqs = await loadJoinRequests(data.group.id);
      setJoinRequests(reqs);
    } catch { setJoinRequests([]); }
    if (user) {
      try {
        const mine = await loadMyPendingRequests(user.id);
        setHasPendingRequest(mine.some((r: any) => r.group_id === data.group.id));
      } catch { setHasPendingRequest(false); }
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, [slug, user?.id]);

  if (notFound) return <Navigate to="/gruppen" replace />;
  if (loading || !group) return <div className="py-20 text-center text-muted-foreground">Lade Gruppe …</div>;

  const myMember = members.find(m => m.user_id === user?.id);
  const isAdmin = myMember?.role === "admin";
  const adminCount = members.filter(m => m.role === "admin").length;

  const copyCode = async () => {
    await navigator.clipboard.writeText(group.invite_code);
    toast.success("Code kopiert! 📋");
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(buildInviteUrl(group.invite_code));
    toast.success("Einladungs-Link kopiert! 🔗");
  };
  const onLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !group) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Max. 2 MB");
    try { await uploadGroupLogo(group.id, file); toast.success("Logo aktualisiert ✅"); reload(); }
    catch (err: any) { toast.error(err.message); }
  };
  const regen = async () => {
    if (!confirm("Neuen Einladungscode generieren? Der alte wird ungültig.")) return;
    try { const code = await regenerateInvite(group.id); toast.success(`Neuer Code: ${code}`); reload(); }
    catch (e: any) { toast.error(e.message); }
  };
  const promote = async (uid: string, role: "admin" | "member") => {
    try { await setMemberRole(group.id, uid, role); toast.success("Rolle aktualisiert"); reload(); }
    catch (e: any) { toast.error(e.message); }
  };
  const kick = async (uid: string, name: string) => {
    if (!confirm(`${name} aus der Gruppe entfernen?`)) return;
    try { await removeMember(group.id, uid); toast.success("Entfernt"); reload(); }
    catch (e: any) { toast.error(e.message); }
  };
  const leave = async () => {
    if (!user) return;
    if (isAdmin && adminCount <= 1) return toast.error("Du bist letzter Admin — befördere zuerst jemand anderen.");
    if (!confirm("Gruppe wirklich verlassen?")) return;
    try { await leaveGroup(group.id, user.id); toast.success("Gruppe verlassen"); window.location.href = "/gruppen"; }
    catch (e: any) { toast.error(e.message); }
  };
  // Friendly message for the per-IP rate limiter. Bulk-approving used to
  // full-reload after each click (~6 API calls each) and trip the 600/min
  // cap; we now patch state in place, but keep a clear hint just in case.
  const errMsg = (e: any) =>
    e?.status === 429
      ? "Zu viele Aktionen in kurzer Zeit — bitte kurz warten und erneut versuchen."
      : (e?.message ?? "Fehler");

  const approve = async (id: string, name: string) => {
    try {
      await approveJoinRequest(id);
      toast.success(`${name} aufgenommen ✅`);
      // In-place update instead of a full reload(): drop the request and
      // append the new member locally. The approve endpoint already did
      // the DB work, so re-fetching the whole group + global ranking +
      // games on every single approval is pure waste — and the thing that
      // tripped the rate limit during fast bulk-approvals.
      const req = joinRequests.find((r) => r.id === id);
      setJoinRequests((prev) => prev.filter((r) => r.id !== id));
      if (req) {
        setMembers((prev) =>
          prev.some((m) => m.user_id === req.user_id)
            ? prev
            : [
                ...prev,
                {
                  group_id: req.group_id,
                  user_id: req.user_id,
                  role: "member",
                  joined_at: new Date().toISOString(),
                  username: req.username,
                  emoji: req.emoji,
                  avatar_url: req.avatar_url ?? null,
                },
              ],
        );
        // Seed a 0-point ranking row so the new member shows immediately;
        // real points fill in on the next natural reload.
        setRows((prev) =>
          prev.some((r) => r.user_id === req.user_id)
            ? prev
            : [
                ...prev,
                {
                  user_id: req.user_id,
                  username: req.username ?? "",
                  emoji: req.emoji ?? "⚽",
                  avatar_url: req.avatar_url ?? null,
                  points: 0,
                  correct: 0,
                  exact: 0,
                },
              ],
        );
      }
    } catch (e: any) {
      toast.error(errMsg(e));
    }
  };
  const reject = async (id: string, name: string) => {
    if (!confirm(`Anfrage von ${name} ablehnen?`)) return;
    try {
      await rejectJoinRequest(id);
      toast.success("Anfrage abgelehnt");
      setJoinRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4">
      <Link to="/gruppen" className="flex items-center gap-1 text-sm text-muted-foreground tap"><ChevronLeft className="h-4 w-4" /> Alle Gruppen</Link>

      {/* Header */}
      <div className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5">
        <div className="flex items-center gap-3">
          <GroupLogo url={group.logo_url} size={64} rounded="lg" emojiClassName="!bg-primary-foreground/10" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-extrabold text-2xl truncate">{group.name}</h1>
            <div className="text-[11px] opacity-80">{members.length} Mitglieder</div>
          </div>
          {isAdmin && (
            <button onClick={() => fileRef.current?.click()} className="bg-primary-foreground text-primary rounded-md p-2 tap" title="Logo hochladen">
              <Upload className="h-4 w-4" />
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogo} />
            </button>
          )}
        </div>
        {group.description && <p className="text-sm opacity-80 mt-3">{group.description}</p>}
      </div>

      {/* Invite – nur für Mitglieder sichtbar */}
      {myMember ? (
        <div className="bg-card rounded-lg shadow-card p-4 space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Einladungscode</div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 bg-muted rounded-md px-3 py-3 text-center font-mono font-extrabold text-2xl tracking-[0.25em]">{group.invite_code}</div>
              <button onClick={copyCode} title="Code kopieren" aria-label="Code kopieren" className="px-3 rounded-md bg-accent text-accent-foreground tap"><Copy className="h-5 w-5" /></button>
              <button onClick={copyLink} title="Einladungslink kopieren" aria-label="Einladungslink kopieren" className="px-3 rounded-md bg-primary text-primary-foreground tap"><Link2 className="h-5 w-5" /></button>
            </div>
          </div>
          <InviteButton variant="cta" groupName={group.name} code={group.invite_code} label="Einladung per WhatsApp senden 📲" />
          <p className="text-[11px] text-muted-foreground text-center">
            Geteilte Links bringen neue Mitglieder direkt in deine Gruppe.
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow-card p-4 space-y-3 text-center">
          <div className="text-3xl">🔒</div>
          <div className="font-display font-extrabold text-lg leading-tight">Du bist kein Mitglied</div>
          <p className="text-[12px] text-muted-foreground">
            Frag eine Mitgliedschaft an — ein Admin der Gruppe muss deine Anfrage bestätigen.
          </p>
          {hasPendingRequest ? (
            <div className="rounded-md bg-muted border border-border p-3 text-sm font-bold inline-flex items-center gap-2">
              <Clock className="h-4 w-4" /> Anfrage gesendet — warte auf Freigabe
            </div>
          ) : (
            <Button
              disabled={requestBusy || !user}
              onClick={async () => {
                if (!user) return;
                setRequestBusy(true);
                try {
                  const r = await requestJoinGroupById(group.id);
                  if (r.status === "already_member") toast.success("Du bist bereits Mitglied");
                  else toast.success("Anfrage gesendet ⏳");
                  setHasPendingRequest(true);
                  reload();
                } catch (e: any) { toast.error(e.message); }
                setRequestBusy(false);
              }}
              className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
            >
              {requestBusy ? "Sende …" : "Mitgliedschaft anfordern"}
            </Button>
          )}
        </div>
      )}

      {/* "Dein Beitrag zur Gruppe" card removed — the metric was confusing
          (zero values when there were no scored games yet) and duplicated
          info already visible in the group ranking below. Component file
          stays in the repo if we want to bring it back later. */}

      {/* Group ranking */}
      <section>
        <h2 className="font-display font-bold text-lg mb-2">Gruppen-Ranking 🏆</h2>
        <div className="bg-card rounded-lg shadow-card overflow-hidden">
          {rows.length === 0 && <div className="p-6 text-center text-muted-foreground">Noch keine Punkte.</div>}
          {rows.map((r, i) => {
            const me = user?.id === r.user_id;
            return (
              <motion.div key={r.user_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay: i*0.02}} className={`flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 ${me ? "bg-accent/30" : ""}`}>
                <div className="w-14 shrink-0 text-center font-display font-extrabold text-xs tabular-nums">#{i+1}</div>
                <UserAvatar url={r.avatar_url} emoji={r.emoji} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{r.username} {me && <span className="text-[10px] text-accent-foreground bg-accent rounded-pill px-1.5 py-0.5">DU</span>}</div>
                  <div className="text-[10px] text-muted-foreground">{r.exact} 🎯 · {r.correct} ✓</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-lg tabular-nums">{r.points}</div>
                  <div className="text-[10px] text-muted-foreground">Punkte</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Beitrittsanfragen (nur Admins) */}
      {isAdmin && joinRequests.length > 0 && (
        <section>
          <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
            <Clock className="h-5 w-5 text-accent-foreground" />
            Beitrittsanfragen ({joinRequests.length})
          </h2>
          <div className="bg-card rounded-lg shadow-card divide-y divide-border">
            {joinRequests.map(r => (
              <div key={r.id} className="px-3 py-2.5 flex items-center gap-3">
                <UserAvatar url={r.avatar_url ?? null} emoji={r.emoji} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{r.username ?? "—"}</div>
                  <div className="text-[10px] text-muted-foreground">möchte beitreten</div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => approve(r.id, r.username ?? "User")}
                    className="inline-flex items-center gap-1 text-[11px] font-bold bg-accent text-accent-foreground rounded-pill px-2.5 py-1 tap"
                    title="Annehmen"
                  >
                    <Check className="h-3.5 w-3.5" /> Annehmen
                  </button>
                  <button
                    onClick={() => reject(r.id, r.username ?? "User")}
                    className="inline-flex items-center gap-1 text-[11px] font-bold bg-muted rounded-pill px-2.5 py-1 tap text-destructive"
                    title="Ablehnen"
                  >
                    <X className="h-3.5 w-3.5" /> Ablehnen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Members admin */}
      <section>
        <h2 className="font-display font-bold text-lg mb-2">Mitglieder ({members.length})</h2>
        <div className="bg-card rounded-lg shadow-card divide-y divide-border">
          {members.map(m => (
            <div key={m.user_id} className="px-3 py-2.5 flex items-center gap-3">
              <UserAvatar url={m.avatar_url ?? null} emoji={m.emoji} size={36} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate flex items-center gap-1">
                  {m.username ?? "—"}
                  {m.role === "admin" && <Crown className="h-3 w-3 text-accent-foreground" />}
                </div>
                <div className="text-[10px] text-muted-foreground">{m.role === "admin" ? "Admin" : "Mitglied"}</div>
              </div>
              {isAdmin && m.user_id !== user?.id && (
                <div className="flex gap-1">
                  {m.role === "member" ? (
                    <button onClick={() => promote(m.user_id, "admin")} className="text-[10px] font-bold bg-accent text-accent-foreground rounded-pill px-2 py-1 tap">→ Admin</button>
                  ) : (
                    <button onClick={() => promote(m.user_id, "member")} className="text-[10px] font-bold bg-muted rounded-pill px-2 py-1 tap">→ Mitglied</button>
                  )}
                  <button onClick={() => kick(m.user_id, m.username ?? "User")} className="text-destructive p-1 tap" title="Entfernen"><UserMinus className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {myMember && (
        <Button variant="outline" onClick={leave} className="w-full text-destructive border-destructive/30">
          <LogOut className="h-4 w-4 mr-2" /> Gruppe verlassen
        </Button>
      )}
    </div>
  );
}
